import { FinalTournamentView } from '../components/scoring/FinalTournamentView';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ApiError, apiFetch } from '../lib/api';
import { useScoringTournament } from '../hooks/useScoringTournament';
import { findShooterByKey, scoringRound2Path, scoringRound2ShooterPath } from '../lib/scoring-routes';
import { scoringTitle } from '../lib/page-title';
import { WHOLE_SHOT_MESSAGE } from '../lib/shooting';
import { draftTenths, wholeDraftTenths, runningTotal } from '../lib/shot-entry';
import { strictWholeShotEntry, showShotPoints } from '../lib/round1';
import { ROUND2_SHOTS, tournamentHasRound2, type Round2AdminView } from '../lib/round2';
import { round2GenderLabel } from '../components/scoring/Round2SetupCard';
import { completedShooters, nextTeamShooter } from '../lib/scoring-navigation';

type CellStatus = 'empty' | 'typing' | 'saving' | 'saved' | 'error';

interface Cell {
  draft: string;
  inputRejected?: boolean;
  savedTenths: number | null;
  scoreId: string | null;
  status: CellStatus;
  error: string | null;
}

function cellsFrom(scores: Round2AdminView['scores'], shooterId: string, count: number): Record<string, Cell> {
  const cells: Record<string, Cell> = {};
  for (let shot = 1; shot <= count; shot++) {
    cells[String(shot)] = { draft: '', savedTenths: null, scoreId: null, status: 'empty', error: null };
  }
  for (const score of scores) {
    if (score.round2ShooterId !== shooterId) continue;
    const key = String(score.shotNumber);
    if (!cells[key]) continue;
    const tenths = draftTenths(score.pointsDisplay);
    cells[key] = {
      draft: tenths == null ? score.pointsDisplay : showShotPoints(tenths, true),
      savedTenths: tenths,
      scoreId: score.id,
      status: 'saved',
      error: null,
    };
  }
  return cells;
}

/** /admin/scoring/:slug/round2/shooter/:shooterKey — the same 5-box shot entry as Round 1, for Round 2 only. */
export function Round2ShooterPage() {
  const { slug: slugParam, shooterKey = '' } = useParams();
  const navigate = useNavigate();
  const { slug, detail, notFound, error } = useScoringTournament(slugParam);
  const tournamentId = detail?.tournament.id;
  const rifle = detail ? tournamentHasRound2(detail.tournament) : false;
  const [view, setView] = useState<Round2AdminView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!tournamentId || !rifle) return;
    let cancelled = false;
    apiFetch<{ round2: Round2AdminView }>(`/api/admin/shooting/tournaments/${tournamentId}/round2`)
      .then((body) => {
        if (!cancelled) setView(body.round2);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : 'Could not load Round 2.');
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentId, rifle]);

  const shooter = useMemo(
    () => (view ? findShooterByKey(view.shooters.map((row) => ({ ...row, sortOrder: 0 })), shooterKey) : null),
    [view, shooterKey],
  );
  const shooterId = shooter?.id ?? null;
  const shotsPerShooter = view?.shotsPerShooter ?? ROUND2_SHOTS;
  const shotNumbers = Array.from({ length: shotsPerShooter }, (_, index) => index + 1);

  useEffect(() => {
    document.title = scoringTitle(shooter?.name, 'Round 2', detail?.tournament.title);
  }, [shooter?.name, detail?.tournament.title]);

  const [cells, setCells] = useState<Record<string, Cell>>({});
  const [preparedFor, setPreparedFor] = useState<string | null>(null);
  const inputs = useRef<Map<string, HTMLInputElement>>(new Map());
  const pending = useRef<Map<string, Promise<void>>>(new Map());
  const [saveCount, setSaveCount] = useState(0);
  const activeShooter = useRef(shooterId);
  activeShooter.current = shooterId;
  const pendingDrafts = useRef(new Map<string, string>());
  const cellsRef = useRef(cells);
  cellsRef.current = cells;

  useEffect(() => {
    const key = `${shooterId}:${shotsPerShooter}`;
    if (!view || !shooterId || preparedFor === key) return;
    setPreparedFor(key);
    setCells(cellsFrom(view.scores, shooterId, shotsPerShooter));
    pending.current.clear();
  }, [view, shooterId, preparedFor, shotsPerShooter]);

  const patchCell = useCallback((key: string, patch: Partial<Cell>) => {
    const current = cellsRef.current;
    if (!current[key]) return;
    const next = { ...current, [key]: { ...current[key], ...patch } };
    cellsRef.current = next;
    setCells(next);
  }, []);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (pending.current.size > 0 || Object.values(cellsRef.current).some((cell) => ['typing', 'saving', 'error'].includes(cell.status))) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  const order = shotNumbers.map(String);
  const listPath = scoringRound2Path(slug);
  const roster = view?.shooters ?? [];
  const index = roster.findIndex((row) => row.id === shooterId);
  const teams = (view?.teams ?? []).map((team) => ({ teamId: team.id, name: team.name, members: roster.filter((s) => s.teamId === team.id).map((s) => ({ shooterId: s.id })) }));
  const teamMode = Boolean(shooter?.teamId);
  const teamNext = shooterId ? nextTeamShooter(roster, teams, shooterId, completedShooters((view?.scores ?? []).map((score) => ({ ...score, shooterId: score.round2ShooterId })), shotsPerShooter)) : null;
  const next = teamMode ? teamNext?.shooter ?? null : index >= 0 ? roster[index + 1] ?? null : null;
  const unsaved = saveCount > 0 || order.some((key) => ['typing', 'saving', 'error'].includes(cells[key]?.status ?? ''));

  function focusAfter(key: string) {
    const at = order.indexOf(key);
    const nextKey = order[at + 1];
    const node = nextKey ? inputs.current.get(nextKey) : null;
    if (node) node.focus();
    else inputs.current.get(key)?.blur();
  }

  function commit(shot: number, draftOverride?: string): Promise<void> {
    const key = String(shot);
    if (cellsRef.current[key]?.inputRejected) return Promise.resolve();
    const draft = (draftOverride ?? cellsRef.current[key]?.draft ?? '').trim();
    const identity = `${shooterId}:${key}`;
    if (pendingDrafts.current.get(identity) === draft && pending.current.has(identity)) return pending.current.get(identity)!;
    const previous = pending.current.get(identity) ?? Promise.resolve();
    const task = previous.catch(() => undefined).then(async () => {
      if (!tournamentId || !shooterId) return;
      if (activeShooter.current !== shooterId) return;
      const patchCurrent = (patch: Partial<Cell>) => { if (activeShooter.current === shooterId) patchCell(key, patch); };
      const cell = cellsRef.current[key];
      if (!cell) return;
      const unchanged = () => activeShooter.current === shooterId && !cellsRef.current[key]?.inputRejected && cellsRef.current[key]?.draft.trim() === draft;
      try {
        if (!draft) {
          if (unchanged()) patchCurrent({ status: 'saving', error: null });
          if (cell.scoreId) await apiFetch(`/api/admin/shooting/round2/scores/${cell.scoreId}?ack=persisted`, { method: 'DELETE' });
          setView((current) => current ? { ...current, scores: current.scores.filter((score) => score.id !== cell.scoreId) } : current);
          patchCurrent({ savedTenths: null, scoreId: null, ...(unchanged() ? { status: 'empty' as const, error: null } : {}) });
          return;
        }
        const tenths = wholeDraftTenths(draft);
        if (tenths == null) { if (unchanged()) patchCurrent({ status: 'error', error: WHOLE_SHOT_MESSAGE }); return; }
        if (unchanged()) patchCurrent({ status: 'saving', error: null });
        const body = await apiFetch<{ score: { id: string; round2ShooterId: string; shotNumber: number; pointsDisplay: string } }>(
          `/api/admin/shooting/tournaments/${tournamentId}/round2/scores?ack=persisted`,
          { method: 'POST', body: JSON.stringify({ shooterId, shotNumber: shot, points: showShotPoints(tenths, true) }) },
        );
        setView((current) => current ? { ...current, scores: [...current.scores.filter((score) => score.round2ShooterId !== shooterId || score.shotNumber !== shot), body.score] } : current);
        patchCurrent({ savedTenths: tenths, scoreId: body.score.id, ...(unchanged() ? { draft: showShotPoints(tenths, true), status: 'saved' as const, error: null } : {}) });
      } catch (err) {
        if (unchanged()) patchCurrent({ status: 'error', error: err instanceof ApiError ? err.message : 'Not saved. Check the connection and retry.' });
      }
    }).finally(() => { if (pending.current.get(identity) === task) { pending.current.delete(identity); pendingDrafts.current.delete(identity); setSaveCount(pending.current.size); } });
    pending.current.set(identity, task);
    setSaveCount(pending.current.size);
    pendingDrafts.current.set(identity, draft);
    return task;
  }

  function onShotChange(shot: number, raw: string) {
    const key = String(shot);
    const { draft, advance, rejected } = strictWholeShotEntry(raw, cellsRef.current[key]?.draft ?? '');
    patchCell(key, { draft, inputRejected: rejected, status: rejected ? 'error' : draft ? 'typing' : 'empty', error: rejected ? WHOLE_SHOT_MESSAGE : null });
    if (advance) {
      void commit(shot, draft);
      focusAfter(key);
    }
  }

  const typed = runningTotal(order.map((key) => cells[key]?.draft ?? ''), shotsPerShooter);
  const savedCount = order.filter((key) => cells[key]?.status === 'saved').length;

  if (detail?.tournament.status === 'final') return <FinalTournamentView detail={detail} round2={view} />;

  return (
    <Layout>
      <div className="mx-auto max-w-xl space-y-4">
        <nav>
          <Link to={listPath} aria-disabled={unsaved} onClick={(event) => { if (unsaved) event.preventDefault(); }} className="inline-flex min-h-11 items-center text-sm font-medium text-[var(--color-accent)]">
            ← Round 2
          </Link>
        </nav>
        {error || loadError ? (
          <p className="rounded-[var(--radius-md)] bg-[var(--color-error-subtle)] px-4 py-3 text-sm text-[var(--color-error-text)]">
            {loadError || error}
          </p>
        ) : null}
        {notFound ? <p className="text-sm">There is no tournament called “{slug}”.</p> : null}
        {detail && !rifle ? <p className="text-sm">Round 2 is only for rifle shooting.</p> : null}
        {view && !shooter ? (
          <p className="text-sm text-[var(--color-text-secondary)]">No Round 2 shooter with the number “{shooterKey}”.</p>
        ) : null}
        {shooter ? (
          <>
            <header className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-[var(--color-text-tertiary)]">Round 2 · {detail?.tournament.title}</p>
                <h1 className="truncate text-2xl font-semibold text-[var(--color-text-primary)]">{shooter.name}</h1>
                <p className="text-sm text-[var(--color-text-tertiary)]">
                  {round2GenderLabel(shooter.gender)}
                  {shooter.bib ? ` · ${shooter.bib}` : ''}
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-semibold tabular-nums" aria-live="polite">
                  {showShotPoints(typed.tenths, true)}
                </p>
                <p className="text-xs text-[var(--color-text-tertiary)]">max {shotsPerShooter * 10}</p>
              </div>
            </header>
            <section className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)] p-4">
              <p className="text-sm text-[var(--color-text-secondary)]">
                Whole numbers 0–10. All {shotsPerShooter} shots count. For 1, press Tab or Enter to move on.
              </p>
              <div className="grid grid-cols-5 gap-2">
                {shotNumbers.map((shot) => {
                  const key = String(shot);
                  const cell = cells[key];
                  const status = cell?.status ?? 'empty';
                  const border =
                    status === 'error'
                      ? 'border-[var(--color-error)] bg-[var(--color-error-subtle)]'
                      : status === 'saved'
                        ? 'border-[var(--color-success)]'
                        : status === 'typing' || status === 'saving'
                          ? 'border-[var(--color-accent)]'
                          : 'border-[var(--color-border-strong)]';
                  return (
                    <label key={key} className="block">
                      <span className="flex justify-between px-0.5 text-xs text-[var(--color-text-tertiary)]">
                        <span>{shot}</span>
                        <span className={status === 'saved' ? 'text-[var(--color-success-text)]' : ''}>{status === 'saved' ? '✓' : status === 'saving' ? '…' : status === 'error' ? '!' : ''}</span>
                      </span>
                      <input
                        ref={(element) => {
                          if (element) inputs.current.set(key, element);
                          else inputs.current.delete(key);
                        }}
                        aria-label={`Shot ${shot}`}
                        aria-invalid={status === 'error'}
                        inputMode="numeric"
                        enterKeyHint="next"
                        autoComplete="off"
                        maxLength={16}
                        placeholder="–"
                        value={cell?.draft ?? ''}
                        onChange={(event) => onShotChange(shot, event.target.value)}
                        onBlur={() => {
                          const current = cellsRef.current[key];
                          if (current && (current.status === 'typing' || (current.status === 'empty' && (current.scoreId || pending.current.has(`${shooterId}:${key}`))))) void commit(shot);
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter') return;
                          event.preventDefault();
                          void commit(shot);
                          focusAfter(key);
                        }}
                        onFocus={(event) => event.target.select()}
                        className={`h-14 w-full rounded-[var(--radius-md)] border-2 bg-[var(--color-surface-1)] text-center text-lg font-semibold tabular-nums ${border}`}
                      />
                    </label>
                  );
                })}
              </div>
              <p className="text-sm text-[var(--color-text-secondary)]">
                {savedCount} of {shotsPerShooter} shots saved · total {showShotPoints(typed.tenths, true)}
              </p>
              {order.some((key) => cells[key]?.status === 'error') ? (
                <p className="text-sm text-[var(--color-error-text)]">{cells[order.find((key) => cells[key]?.status === 'error') ?? '']?.error ?? WHOLE_SHOT_MESSAGE}</p>
              ) : null}
            </section>
            <div className="grid grid-cols-2 gap-2">
              <Link to={listPath} aria-disabled={unsaved} onClick={(event) => { if (unsaved) event.preventDefault(); }} className="inline-flex min-h-12 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-surface-2)] text-sm font-medium">
                Back to Round 2
              </Link>
              {next ? (
                <button
                  type="button"
                  onClick={() => navigate(scoringRound2ShooterPath(slug, next))}
                  disabled={unsaved}
                  className="min-h-12 truncate rounded-[var(--radius-md)] bg-[var(--color-accent)] px-3 text-sm font-medium text-white"
                >
                  {teamNext?.sameTeam ? 'Next teammate: ' : teamNext?.team ? `Next team: ${teamNext.team.name} · ` : 'Next: '}{next.name}
                </button>
              ) : (
                <span className="inline-flex min-h-12 items-center justify-center text-sm text-[var(--color-text-tertiary)]">{teamMode && savedCount < shotsPerShooter ? 'Finish this shooter to continue' : 'Last shooter'}</span>
              )}
            </div>
          </>
        ) : null}
      </div>
    </Layout>
  );
}
